import { useState } from "react";
import { toast } from "sonner";
import { errorMessage, put } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";

const KEYS = ["open_jobs", "run_updates", "job_taken", "unfilled", "daily_summary", "trial_reminder"];
const OPTIONS = {
  locksmith: [
    ["open_jobs", "New open jobs", "When a job any locksmith can accept is posted, plus a reminder the evening before if it's still free."],
    ["run_updates", "My runs", "When a run is assigned to you, its date or start time changes, or it's cancelled."],
  ],
  manager: [
    ["daily_summary", "Daily summary", "Each morning at 7am: today's runs and open jobs for the next 7 days."],
    ["job_taken", "Job taken", "When a locksmith accepts one of your open jobs."],
    ["unfilled", "Unfilled job reminders", "3 days, 2 days and the evening before an open job that nobody has taken."],
    ["trial_reminder", "Trial ending", "A reminder 3 days before your free trial ends."],
  ],
};

export const EmailPrefsCard = ({ Card }) => {
  const { profile, refreshProfile } = useAuth();
  const [prefs, setPrefs] = useState(() => Object.fromEntries(KEYS.map((k) => [k, profile.email_prefs?.[k] !== false])));
  const [saving, setSaving] = useState(null);

  const toggle = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setSaving(key);
    try {
      await put("/me/email-prefs", next);
      await refreshProfile();
      toast.success(value ? "Email turned on" : "Email turned off");
    } catch (e) {
      setPrefs(prefs);
      toast.error(errorMessage(e));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card title="Email notifications" testId="profile-email-prefs-card">
      <p className="text-sm text-slate-600">Choose which emails you get. Account emails, like email confirmation, password and invites, are always sent.</p>
      <ul className="divide-y divide-slate-100">
        {(OPTIONS[profile.role] || OPTIONS.locksmith).map(([key, label, help]) => (
          <li key={key} className="flex items-start justify-between gap-4 py-3">
            <label htmlFor={`pref-${key}`} className="cursor-pointer">
              <span className="block text-sm font-semibold text-slate-900">{label}</span>
              <span className="block text-xs text-slate-500">{help}</span>
            </label>
            <Switch id={`pref-${key}`} checked={prefs[key]} disabled={saving === key} onCheckedChange={(v) => toggle(key, v)} data-testid={`email-pref-${key}`} />
          </li>
        ))}
      </ul>
    </Card>
  );
};
